import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PromiseStatuses } from "../../../../lib/utils";
import { CreateLegalRepresentativeDTO, FindAllLegalRepresentativesFiltersDTO, FindAllLegalRepresentativesResponseDTO, LegalRepresentativesResponseDTO, UpdateLegalRepresentativeDTO } from "./dto";
import { NewLegalRepresentativeService } from "./service";

interface LegalRepresentativeState {
    selectedLegalRepresentativeCreationTab: number
    selectedLegalRepresentativeUpdatedTab: number
    findAllLegalRepresentativesStatus: PromiseStatuses
    findAllLegalRepresentativesResponse?: FindAllLegalRepresentativesResponseDTO
    getLegalRepresentativeByIdStatus: PromiseStatuses
    getLegalRepresentativeByIdResponse?: LegalRepresentativesResponseDTO
    getLegalRepresentativeByIdsStatus: PromiseStatuses
    getLegalRepresentativeByIdsResponse?: LegalRepresentativesResponseDTO[]
    findAllLegalRepresentativesFilters: FindAllLegalRepresentativesFiltersDTO
    legalRepresentativeCreateRequest: CreateLegalRepresentativeDTO
    legalRepresentativeCreateStatus: PromiseStatuses
    legalRepresentativeUpdateStatus: PromiseStatuses
    legalRepresentativeUpdateRequest: UpdateLegalRepresentativeDTO
    legalRepresentativeCreateErrors: Partial<Record<keyof CreateLegalRepresentativeDTO, string>>
    removeLegalRepresentativeStatus: PromiseStatuses
    deleteLegalRepresentativeStatus: PromiseStatuses
}

const initialState: LegalRepresentativeState = {
    selectedLegalRepresentativeCreationTab: 0,
    selectedLegalRepresentativeUpdatedTab: 0,
    findAllLegalRepresentativesStatus: "idle",
    getLegalRepresentativeByIdStatus: "idle",
    findAllLegalRepresentativesFilters: {
        itemsPerPage: 0,
        page: 0,
        order: null
    },
    legalRepresentativeCreateRequest: {
        fiscalCode: "",
        surname: "",
        name: "",
        sex: null,
        birthdate: null,
        state: "",
        countryRegion: "",
        province: "",
        city: "",
        contactAddress: "",
        contactCountryRegion: "",
        contactProvince: "",
        contactCity: "",
        contactZipCode: "",
        email: "",
        phone: "",
        mobilePhone: "",
        fax: "",
        activationDate: null,
        deactivationDate: null,
        note: "",
        customerId: ""
    },
    legalRepresentativeCreateStatus: "idle",
    legalRepresentativeUpdateStatus: "idle",
    legalRepresentativeUpdateRequest: {
        fiscalCode: "",
        surname: "",
        name: "",
        sex: null,
        birthdate: null,
        state: "",
        countryRegion: "",
        province: "",
        city: "",
        contactAddress: "",
        contactCountryRegion: "",
        contactProvince: "",
        contactCity: "",
        contactZipCode: "",
        email: "",
        phone: "",
        mobilePhone: "",
        fax: "",
        activationDate: null,
        deactivationDate: null,
        note: "",
        customerId: ""
    },
    legalRepresentativeCreateErrors: {},
    removeLegalRepresentativeStatus: "idle",
    deleteLegalRepresentativeStatus: "idle",
    getLegalRepresentativeByIdsStatus: "idle"
}

export const validateLegalRepresentativeCreateRequest = createAsyncThunk(
    'legalRepresentative/validateCreateRequest',
    async (request: CreateLegalRepresentativeDTO, thunkApi): Promise<void> => {
        const errors: Partial<Record<keyof CreateLegalRepresentativeDTO, string>> = {}

        const requiredStrings: { field: keyof CreateLegalRepresentativeDTO; label: string }[] = [
            { field: 'fiscalCode', label: 'Codice Fiscale' },
            { field: 'surname', label: 'Cognome' },
            { field: 'name', label: 'Nome' },
            { field: 'birthdate', label: 'Data di nascita' },
            { field: 'sex', label: 'Sesso' },
            { field: 'countryRegion', label: 'Regione' },
            { field: 'province', label: 'Provincia' },
            { field: 'city', label: 'Comune' },
            { field: 'contactAddress', label: 'Indirizzo di Contatto' },
            { field: 'contactCountryRegion', label: 'Regione di Contatto' },
            { field: 'contactProvince', label: 'Provincia di Contatto' },
            { field: 'contactCity', label: 'Comune di Contatto' },
            { field: 'contactZipCode', label: 'CAP di Contatto' },
            { field: 'email', label: 'Email' },
            { field: 'phone', label: 'Telefono' },
            { field: 'mobilePhone', label: 'Cellulare' },
            { field: 'customerId', label: 'Cliente' },
        ]

        for (const { field, label } of requiredStrings) {
            const value = request[field]
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                errors[field] = `${label} è obbligatorio`
            }
        }

        const fiscalCode = request.fiscalCode?.trim() ?? ''
        if (fiscalCode && fiscalCode.length < 16) {
            errors.fiscalCode = 'Codice Fiscale deve avere almeno 16 caratteri'
        }

        const phone = request.phone?.trim() ?? ''
        if (phone && (phone.length < 7 || phone.length > 13)) {
            errors.phone = 'Telefono deve avere tra 7 e 13 caratteri'
        }

        const mobilePhone = request.mobilePhone?.trim() ?? ''
        if (mobilePhone && (mobilePhone.length < 7 || mobilePhone.length > 13)) {
            errors.mobilePhone = 'Cellulare deve avere tra 7 e 13 caratteri'
        }

        console.log(request)

        if (!request.activationDate) {
            errors.activationDate = 'Data Ingresso è obbligatoria'
        }

        if (Object.keys(errors).length > 0) {
            return thunkApi.rejectWithValue(errors) as never
        }
    }
)

export const createLegalRepresentative = createAsyncThunk(
    'legalRepresentative/createLegalRepresentative',
    async (request: CreateLegalRepresentativeDTO, thunkApi): Promise<number> => {
        const legalRepresentativeService = NewLegalRepresentativeService()

        return legalRepresentativeService.createLegalRepresentative(request).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })

    },
)

export const updateLegalRepresentative = createAsyncThunk(
    'legalRepresentative/updateLegalRepresentative',
    async (request: { id: string, data: UpdateLegalRepresentativeDTO }, thunkApi): Promise<void> => {
        const legalRepresentativeService = NewLegalRepresentativeService()

        return legalRepresentativeService.updateLegalRepresentative(request.id, request.data).catch(error => {
            throw (thunkApi.rejectWithValue(error))
        })

    },
)

export const findAllLegalRepresentatives = createAsyncThunk(
    'legalRepresentative/findAllLegalRepresentatives',
    async (filters: FindAllLegalRepresentativesFiltersDTO, thunkApi): Promise<FindAllLegalRepresentativesResponseDTO> => {
        const legalRepresentativeService = NewLegalRepresentativeService()

        return legalRepresentativeService.findAllLegalRepresentatives(filters)
    },
)

export const getLegalRepresentativeById = createAsyncThunk(
    'legalRepresentative/getLegalRepresentativeById',
    async (id: string, thunkApi): Promise<LegalRepresentativesResponseDTO> => {
        const legalRepresentativeService = NewLegalRepresentativeService()

        return legalRepresentativeService.getLegalRepresentativeById(id)
    },
)

export const getLegalRepresentativeByIds = createAsyncThunk(
    'legalRepresentative/getLegalRepresentativeByIds',
    async (request: string[], thunkApi): Promise<LegalRepresentativesResponseDTO[]> => {
        const legalRepresentativeService = NewLegalRepresentativeService()

        const promises: Promise<LegalRepresentativesResponseDTO>[] = request.map((id) =>

            legalRepresentativeService.getLegalRepresentativeById(id)
        )
        return Promise.all(promises)
    }
)

export const removeLegalRepresentative = createAsyncThunk(
    'legalRepresentative/removeLegalRepresentative',
    async (id: string, thunkApi): Promise<void> => {
        const legalRepresentativeService = NewLegalRepresentativeService()

        return legalRepresentativeService.removeLegalRepresentative(id)
    },
)

export const deleteLegalRepresentative = createAsyncThunk(
    'legalRepresentative/deleteLegalRepresentative',
    async (id: string, thunkApi): Promise<void> => {
        const legalRepresentativeService = NewLegalRepresentativeService()

        return legalRepresentativeService.deleteLegalRepresentative(id)
    },
)

const legalRepresentativeSlice = createSlice({
    name: 'legalRepresentative/slice',
    initialState,
    reducers: {
        setLegalRepresentativeCreateRequestField: (state, action: { payload: { field: keyof CreateLegalRepresentativeDTO, value: any } }) => {
            state.legalRepresentativeCreateRequest = {
                ...state.legalRepresentativeCreateRequest,
                [action.payload.field]: action.payload.value
            }
        },
        setLegalRepresentativeUpdateRequest: (state, action) => {
            state.legalRepresentativeUpdateRequest = action.payload
        },
        setLegalRepresentativeUpdateRequestField: (state, action: { payload: { field: keyof UpdateLegalRepresentativeDTO, value: any } }) => {
            state.legalRepresentativeUpdateRequest = {
                ...state.legalRepresentativeUpdateRequest,
                [action.payload.field]: action.payload.value
            }
        },
        setFindAllLegalRepresentativeFilterField: (state, action: { payload: { field: keyof FindAllLegalRepresentativesFiltersDTO, value: any } }) => {
            state.findAllLegalRepresentativesFilters = {
                ...state.findAllLegalRepresentativesFilters,
                [action.payload.field]: action.payload.value
            }
        },
        setDeleteLegalRepresentativeStatus: (state, action) => {
            state.deleteLegalRepresentativeStatus = action.payload
        },
        resetLegalRepresentativeCreateRequest: (state) => {
            state.legalRepresentativeCreateRequest = initialState.legalRepresentativeCreateRequest
        },
        resetLegalRepresentativeUpdateRequest: (state) => {
            state.legalRepresentativeUpdateRequest = initialState.legalRepresentativeUpdateRequest
        },
        setLegalRepresentativeCreateErrors: (state, action: { payload: Partial<Record<keyof CreateLegalRepresentativeDTO, string>> }) => {
            state.legalRepresentativeCreateErrors = action.payload
        },
        resetLegalRepresentativeCreateErrors: (state) => {
            state.legalRepresentativeCreateErrors = {}
        },
        resetLegalRepresentativeCreateError: (state, action: { payload: keyof CreateLegalRepresentativeDTO }) => {
            delete state.legalRepresentativeCreateErrors[action.payload]
        },
        resetLegalRepresentativeByIds: (state) => {
            state.getLegalRepresentativeByIdsResponse = undefined
            state.getLegalRepresentativeByIdsStatus = 'idle'
        },
        setLegalRepresentativeCreateStatus: (state, action) => {
            state.legalRepresentativeCreateStatus = action.payload
        },
        setLegalRepresentativeUpdateStatus: (state, action) => {
            state.legalRepresentativeUpdateStatus = action.payload
        },
        setRemoveLegalRepresentativeStatus: (state, action) => {
            state.removeLegalRepresentativeStatus = action.payload
        },
    },
    extraReducers(builder) {
        builder
            .addCase(findAllLegalRepresentatives.pending, (state) => {
                state.findAllLegalRepresentativesStatus = 'loading'
            })
            .addCase(findAllLegalRepresentatives.fulfilled, (state, action) => {
                state.findAllLegalRepresentativesStatus = 'successfully'
                state.findAllLegalRepresentativesResponse = action.payload
            })
            .addCase(findAllLegalRepresentatives.rejected, (state) => {
                state.findAllLegalRepresentativesStatus = 'failed'
            })
            .addCase(getLegalRepresentativeById.pending, (state) => {
                state.getLegalRepresentativeByIdStatus = 'loading'
            })
            .addCase(getLegalRepresentativeById.fulfilled, (state, action) => {
                state.getLegalRepresentativeByIdStatus = 'successfully'
                state.getLegalRepresentativeByIdResponse = action.payload
            })
            .addCase(getLegalRepresentativeById.rejected, (state) => {
                state.getLegalRepresentativeByIdStatus = 'failed'
            })
            .addCase(createLegalRepresentative.pending, (state) => {
                state.legalRepresentativeCreateStatus = 'loading'
            })
            .addCase(createLegalRepresentative.fulfilled, (state, action) => {
                state.legalRepresentativeCreateStatus = 'successfully'
            })
            .addCase(createLegalRepresentative.rejected, (state) => {
                state.legalRepresentativeCreateStatus = 'failed'
            })
            .addCase(updateLegalRepresentative.pending, (state) => {
                state.legalRepresentativeUpdateStatus = 'loading'
            })
            .addCase(updateLegalRepresentative.fulfilled, (state, action) => {
                state.legalRepresentativeUpdateStatus = 'successfully'
            })
            .addCase(updateLegalRepresentative.rejected, (state) => {
                state.legalRepresentativeUpdateStatus = 'failed'
            })
            .addCase(removeLegalRepresentative.pending, (state) => {
                state.removeLegalRepresentativeStatus = 'loading'
            })
            .addCase(removeLegalRepresentative.fulfilled, (state, action) => {
                state.removeLegalRepresentativeStatus = 'successfully'
            })
            .addCase(removeLegalRepresentative.rejected, (state) => {
                state.removeLegalRepresentativeStatus = 'failed'
            })
            .addCase(deleteLegalRepresentative.pending, (state) => {
                state.deleteLegalRepresentativeStatus = 'loading'
            })
            .addCase(deleteLegalRepresentative.fulfilled, (state, action) => {
                state.deleteLegalRepresentativeStatus = 'successfully'
            })
            .addCase(deleteLegalRepresentative.rejected, (state) => {
                state.deleteLegalRepresentativeStatus = 'failed'
            })
            .addCase(getLegalRepresentativeByIds.pending, (state) => {
                state.getLegalRepresentativeByIdsStatus = 'loading'
            })
            .addCase(getLegalRepresentativeByIds.fulfilled, (state, action) => {
                state.getLegalRepresentativeByIdsStatus = 'successfully'
                state.getLegalRepresentativeByIdsResponse = action.payload
            })
            .addCase(getLegalRepresentativeByIds.rejected, (state) => {
                state.getLegalRepresentativeByIdsStatus = 'failed'
            })
            .addCase(validateLegalRepresentativeCreateRequest.fulfilled, (state) => {
                state.legalRepresentativeCreateErrors = {}
            })
            .addCase(validateLegalRepresentativeCreateRequest.rejected, (state, action) => {
                state.legalRepresentativeCreateErrors = action.payload ?? {}
            })
    },
})

export const {
    setDeleteLegalRepresentativeStatus,
    setLegalRepresentativeCreateRequestField,
    setLegalRepresentativeUpdateRequest,
    setLegalRepresentativeUpdateRequestField,
    setFindAllLegalRepresentativeFilterField,
    resetLegalRepresentativeCreateRequest,
    resetLegalRepresentativeUpdateRequest,
    setLegalRepresentativeCreateErrors,
    resetLegalRepresentativeCreateErrors,
    resetLegalRepresentativeCreateError,
    resetLegalRepresentativeByIds,
    setLegalRepresentativeCreateStatus,
    setLegalRepresentativeUpdateStatus,
    setRemoveLegalRepresentativeStatus
} = legalRepresentativeSlice.actions

export default legalRepresentativeSlice.reducer